import React, { useState, useEffect } from "react";
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";
import "../App.css";
import { cleanMarkdown } from "../utils/cleanMarkdown";
import { restorePaperNotes } from "../utils/questionPaper";
import { copyToClipboard } from "../utils/clipboard";
import { useApp } from "../contexts/AppContext";
import SaveRubricModal from "./SaveRubricModal";
import MessageBox from "./MessageBox";
import ConfirmDialog from "./ConfirmDialog";
import DuplicateRubricDialog from "./DuplicateRubricDialog";

// Configure PDF.js worker - using jsDelivr for better reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export default function RubricUploader() {
  const { saveRubric: onSaveRubric, currentUser } = useApp();
  const [selectedFile, setSelectedFile] = useState(null);
  const [text, setText] = useState(() => {
    const saved = localStorage.getItem("rubricText") || "";
    return cleanMarkdown(saved);
  });
  const [pdfData, setPdfData] = useState(() => {
    const saved = localStorage.getItem("rubricPdfData");
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  // Duplicate-rubric dialog state. Populated when /rubrics POST returns 409.
  // Carries the existingId + the original save attempt's args so the teacher
  // can confirm a second rubric or open the existing one.
  const [duplicateState, setDuplicateState] = useState({
    open: false, message: '', existingId: null, retryArgs: null, canCreateSecond: false, creatingSecond: false
  });

  // Listen for changes to rubricText in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("rubricText") || "";
      setText(saved);
    };

    // Check for changes periodically (since localStorage events don't fire in same tab)
    const interval = setInterval(() => {
      const saved = localStorage.getItem("rubricText") || "";
      if (saved !== text) {
        setText(saved);
      }
    }, 500);

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [text]);

  // Convert file → Base64 (removed - not currently used)
  // function fileToBase64(file) {
  //   return new Promise((resolve, reject) => {
  //     const r = new FileReader();
  //     r.onload = () => resolve(r.result);
  //     r.onerror = reject;
  //     r.readAsDataURL(file);
  //   });
  // }

  // Extract text and images from PDF
  async function extractPdfContent(file) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    const pdfContent = {
      text: "",
      pages: []
    };

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      
      // Extract text
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(" ");
      pdfContent.text += pageText + "\n";
      
      // Render page as image
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Convert canvas to base64 image
      const imageData = canvas.toDataURL("image/png");
      pdfContent.pages.push({
        pageNumber: pageNum,
        image: imageData.split(",")[1], // Remove data:image/png;base64, prefix
        text: pageText
      });
    }
    
    return pdfContent;
  }

  // ------------------------------
  //   MAIN RUBRIC UPLOADER
  // ------------------------------
  async function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;

    setSelectedFile(f);
    setLoading(true);

    try {
      // ---- 1. PDF: extract text and images
      if (f.type === "application/pdf") {
        const pdfContent = await extractPdfContent(f);
        setPdfData(pdfContent);
        // Store extracted text separately so it can be displayed
        setText(pdfContent.text || "");
        localStorage.setItem("rubricPdfData", JSON.stringify(pdfContent));
        localStorage.setItem("rubricText", pdfContent.text || "");
        
        const textLength = (pdfContent.text || "").trim().length;
        if (textLength > 0) {
          setMessage({ show: true, text: `PDF rubric loaded successfully (${pdfContent.pages.length} pages, ${textLength} characters extracted).`, type: 'success' });
        } else {
          setMessage({ show: true, text: `PDF rubric loaded (${pdfContent.pages.length} pages). Warning: No text extracted - PDF may contain only images.`, type: 'info' });
        }
        return;
      }

      // ---- 2. DOCX → extract → clean
      if (
        f.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        f.name.endsWith(".docx")
      ) {
        const arr = await f.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer: arr });

        const div = document.createElement("div");
        div.innerHTML = result.value;

        const plain = div.textContent || "";
        const cleaned = cleanMarkdown(plain);

        setText(cleaned);
        setPdfData("");
        return;
      }

      // ---- 3. TXT → clean
      const raw = await f.text();
      const cleaned = cleanMarkdown(raw);

      setText(cleaned);
      setPdfData("");
    } catch (err) {
      console.error('Error processing file:', err);
      setMessage({ show: true, text: 'Error processing file. Check console for details.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  // Open save modal
  function openSaveModal() {
    setShowSaveModal(true);
  }

  // Handle save from modal
  async function handleSaveFromModal(formData) {
    const { classSubjectId, class: className, subject, assignmentName, paperType, validUntil, type, createdByUsername } = formData;

    // Convert date to ISO string if provided
    let validUntilISO = null;
    if (validUntil) {
      const date = new Date(validUntil);
      if (!isNaN(date.getTime())) {
        validUntilISO = date.toISOString();
      }
    }

    const metadata = {
      classSubjectId,
      class: className,
      subject,
      assignmentName: assignmentName || '',
      paperType: paperType || '',
      validUntil: validUntilISO,
      type,
      createdByUsername: createdByUsername || ''
    };

    const source = localStorage.getItem("rubricPaperSource") || "";
    const stored = text ? restorePaperNotes(text, source) : text;
    localStorage.setItem("rubricText", text || "");
    localStorage.setItem("rubricPdfData", pdfData ? JSON.stringify(pdfData) : "");

    try {
      await onSaveRubric(stored || pdfData, metadata);
      setShowSaveModal(false);
      setMessage({ show: true, text: 'Rubric saved successfully.', type: 'success' });
    } catch (err) {
      // Backend rejected because a matching class+subject+date rubric
      // exists. Pop the dialog so the teacher can confirm a second one
      // (if only one exists) or open the existing rubric.
      if (err && (err.code === 'DUPLICATE_RUBRIC' || err.code === 'CONFIRM_SECOND_RUBRIC')) {
        setShowSaveModal(false);
        setDuplicateState({
          open: true,
          message: err.message,
          existingId: err.existingId,
          retryArgs: { content: text || pdfData, metadata },
          canCreateSecond: err.code === 'CONFIRM_SECOND_RUBRIC',
          creatingSecond: false
        });
        return;
      }
      console.error('Error saving rubric:', err);
      setMessage({ show: true, text: 'Failed to save rubric. Check console for details.', type: 'error' });
    }
  }

  // Duplicate-rubric dialog handlers
  function closeDuplicateDialog() {
    setDuplicateState({ open: false, message: '', existingId: null, retryArgs: null, canCreateSecond: false, creatingSecond: false });
  }
  async function handleViewExisting() {
    // Copy the rubric content the teacher just tried to save into the
    // clipboard, so they can paste it straight into the existing rubric
    // when they open it. Fall back gracefully if the copy fails — they
    // can still navigate to the existing rubric and edit it manually.
    const content = duplicateState.retryArgs && duplicateState.retryArgs.content;
    let copied = false;
    if (content) {
      // pdfData isn't a plain string; only copy when it's text content.
      const asText = typeof content === 'string' ? content : '';
      if (asText) {
        copied = await copyToClipboard(asText);
      }
    }
    closeDuplicateDialog();
    if (copied) {
      setMessage({ show: true, text: 'New rubric content copied to clipboard. Paste it into the existing rubric in the new tab.', type: 'success' });
    }
    // Open Saved Rubrics in a new tab so the teacher can keep their
    // unsaved draft visible in the original tab while they edit the
    // existing rubric in the new one.
    window.open('/rubrics/saved', '_blank', 'noopener,noreferrer');
  }

  async function handleCreateSecond() {
    const retry = duplicateState.retryArgs;
    if (!retry) return;
    setDuplicateState((prev) => ({ ...prev, creatingSecond: true }));
    try {
      await onSaveRubric(retry.content, { ...retry.metadata, allowSecond: true });
      closeDuplicateDialog();
      setMessage({ show: true, text: 'Second rubric saved successfully.', type: 'success' });
    } catch (err) {
      if (err && (err.code === 'DUPLICATE_RUBRIC' || err.code === 'CONFIRM_SECOND_RUBRIC')) {
        setDuplicateState((prev) => ({
          ...prev,
          message: err.message,
          existingId: err.existingId || prev.existingId,
          canCreateSecond: err.code === 'CONFIRM_SECOND_RUBRIC',
          creatingSecond: false
        }));
        return;
      }
      console.error('Error saving second rubric:', err);
      closeDuplicateDialog();
      setMessage({ show: true, text: 'Failed to save rubric. Check console for details.', type: 'error' });
    }
  }

  // Delete saved rubrics
  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => { 
    confirm.onConfirm(); 
    setConfirm({ show: false, text: '', onConfirm: null }); 
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  function clearRubrics() {
    showConfirm('Are you sure you want to remove all saved rubrics?', () => {
      localStorage.removeItem("rubricText");
      localStorage.removeItem("rubricPdfData");
      localStorage.removeItem("rubricPdf"); // Legacy
      setText("");
      setPdfData(null);
      setSelectedFile(null);
      setMessage({ show: true, text: 'Previous rubrics removed.', type: 'success' });
    });
  }

  // ------------------------------
  //   RENDER UI
  // ------------------------------
  return (
    <div
      className="rubric-uploader"
      style={{ 
        padding: '16px', 
        margin: '0 auto', 
        maxWidth: 1400,
        background: 'var(--surface, #ffffff)'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
          }}>
            <i className="fas fa-file-edit" style={{ color: 'white', fontSize: '20px' }}></i>
          </div>
          <div>
          <h1 style={{ 
              color: 'var(--text-primary)', 
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: '700'
            }}>Edit & Upload Rubric</h1>
            <p style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: '0.75rem'
            }}>Upload or manually edit your grading rubric</p>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(124, 58, 237, 0.05))',
        borderLeft: '3px solid #8B5CF6',
        borderRadius: '10px',
        padding: '12px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          flexShrink: 0
        }}>
          <i className="fas fa-cloud-upload-alt"></i>
        </div>
        
        <label htmlFor="rubric-file-input" style={{
          flex: 1,
          padding: '10px 14px',
          border: '2px dashed #8B5CF6',
          borderRadius: '6px',
          background: 'var(--surface)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)';
          e.currentTarget.style.borderColor = '#7C3AED';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--surface)';
          e.currentTarget.style.borderColor = '#8B5CF6';
        }}>
          <i className="fas fa-file-upload" style={{ fontSize: '1rem', color: '#8B5CF6' }}></i>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
              {selectedFile ? selectedFile.name : 'Click to upload rubric file'}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              PDF, TXT files supported
            </div>
          </div>
          {selectedFile && (
            <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '1.1rem' }}></i>
          )}
        </label>
        <input 
          id="rubric-file-input"
          type="file" 
          accept=".pdf,.txt" 
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </div>

      {pdfData && (
        <div style={{ 
          marginBottom: '16px',
          padding: '12px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
          border: '1px solid #10b981',
          borderRadius: '10px'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '6px'
          }}>
            <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '1.2rem' }}></i>
            <div style={{ color: '#047857', fontWeight: 600, fontSize: '0.8125rem' }}>
              PDF rubric loaded successfully
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#059669', marginLeft: '32px' }}>
            📄 {pdfData.pages?.length || 0} pages with diagrams preserved<br/>
            📝 Extracted text: {(pdfData.text || "").trim().length > 0 
              ? `${(pdfData.text || "").trim().length} characters` 
              : '⚠️ No text found - PDF may contain only images'}
          </div>
        </div>
      )}

      {/* Editable Textbox */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(37, 99, 235, 0.05))',
        borderLeft: '3px solid #3B82F6',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            <i className="fas fa-edit"></i>
          </div>
          <div>
          <h2 style={{ 
              fontSize: '0.9375rem', 
              fontWeight: '700',
              color: 'var(--text-primary)',
              margin: 0
            }}>
              Rubric Content
            </h2>
            <p style={{
              margin: 0,
              fontSize: '0.6875rem',
              color: 'var(--text-muted)'
            }}>
              {pdfData ? 'Extracted from PDF - Edit as needed' : 'Enter or paste your rubric text'}
            </p>
          </div>
        </div>
        <textarea
          value={text}
          onChange={(e) => {
            const value = e.target.value;
            setText(value);
            localStorage.setItem("rubricText", value);
          }}
          style={{
            width: '100%',
            minHeight: 300,
            padding: '16px',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            whiteSpace: 'pre-wrap',
            backgroundColor: 'var(--surface)',
            color: 'var(--text-primary)',
            overflowY: 'auto',
            fontFamily: 'inherit',
            fontSize: '0.8125rem',
            lineHeight: '1.6',
            resize: 'vertical'
          }}
          placeholder="Enter your rubric content here..."
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '16px'
      }}>
        <button 
          onClick={openSaveModal}
          style={{
            padding: '12px 20px',
            fontSize: '0.875rem',
            fontWeight: '700',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
          }}
        >
          <i className="fas fa-save"></i>
          Save Rubric
        </button>
        <button
          onClick={clearRubrics}
          style={{
            padding: '12px 20px',
            fontSize: '0.875rem',
            fontWeight: '600',
            borderRadius: '10px',
            border: 'none',
            background: '#EF4444',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#DC2626';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#EF4444';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
          }}
        >
          <i className="fas fa-trash"></i>
          Clear
        </button>
      </div>

      {loading && (
        <div style={{ 
          marginTop: '24px',
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.1))',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#3B82F6'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.25rem' }}></i>
          <span style={{ fontWeight: '600' }}>Processing file...</span>
        </div>
      )}

      {/* Save Rubric Modal */}
      <SaveRubricModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveFromModal}
        currentUser={currentUser}
      />
      
      <MessageBox 
        message={message} 
        onClose={() => setMessage({ show: false, text: '', type: 'info' })} 
      />
      
      <ConfirmDialog
        confirm={confirm}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <DuplicateRubricDialog
        open={duplicateState.open}
        message={duplicateState.message}
        canCreateSecond={duplicateState.canCreateSecond}
        creatingSecond={duplicateState.creatingSecond}
        onCancel={closeDuplicateDialog}
        onViewExisting={handleViewExisting}
        onCreateSecond={handleCreateSecond}
      />
    </div>
  );
}
